/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import color from "@eisinc/XDL/Utilities/color";
import { onNotPrint } from "@eisinc/XDL/Utilities/print";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import { BreakpointKey, breakpointKeys } from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { PropsWithChildren, useRef, useState } from "react";
import { css } from "styled-components";

type CollapsibleContainerProps = {
    breakpoint: BreakpointKey;
};

const anchorHeight = 55;

const commonCollapsibleContainerStyle = css`
    position: relative;
    padding: 10px 0;
    overflow: hidden;
    border-top: 1px solid ${color("common.ui2")};
`;

const collapsibleContainerStyle = {
    collapsed: {
        css: css`
            ${commonCollapsibleContainerStyle}

            ${onNotPrint(css`
                height: ${anchorHeight}px;

                .accord-anchor {
                    &:after {
                        display: block;
                    }
                }
            `)}
        `,
    },
    expanded: {
        css: css`
            ${commonCollapsibleContainerStyle}
            height: 100%;

            .accord-anchor {
                svg {
                    transform: rotate(180deg);
                }

                &:after {
                    display: none;
                }
            }
        `,
    },
    anchor: {
        css: css`
            display: flex;
            justify-content: flex-end;
            align-items: flex-end;
            position: absolute;
            background: transparent;
            width: 100%;
            height: ${anchorHeight}px;
            top: 0;
            left: 0;
            padding-top: 15px;

            &:focus {
                outline: none;
            }

            svg {
                transition: transform 0.2s ease-in-out;
            }

            &:after {
                display: none;
                position: absolute;
                bottom: 0;
                left: 0;
                content: " ";
                width: 100%;
                height: 30px;
            }
        `,
    },
};

function getNextBreakpoint(currentBreakpointKey: BreakpointKey) {
    const keysLength = breakpointKeys.length;
    let nextItemIndex = 0;
    breakpointKeys.every((key, index) => {
        if (index === keysLength - 1) {
            return false;
        }
        nextItemIndex = index + 1;
        if (key === currentBreakpointKey) {
            return false;
        }
        return true;
    });

    return breakpointKeys[nextItemIndex];
}

const ResponsiveAccordion: React.FC<PropsWithChildren<CollapsibleContainerProps>> = props => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const nextBreakpoint = getNextBreakpoint(props.breakpoint);
    const childernWrapper = useRef<HTMLDivElement>(null);
    const contentHeight = childernWrapper.current?.getBoundingClientRect()?.height;
    const hasHeightOverflow = contentHeight !== undefined ? contentHeight > anchorHeight : true;

    return (
        <>
            <Hidden above={props.breakpoint}>
                <StyledWrapper
                    {...(isCollapsed && hasHeightOverflow
                        ? collapsibleContainerStyle.collapsed
                        : collapsibleContainerStyle.expanded)}
                >
                    {hasHeightOverflow && (
                        <Clickable
                            className="accord-anchor"
                            {...collapsibleContainerStyle.anchor}
                            onClick={() => setIsCollapsed(!isCollapsed)}
                        >
                            <Icon src={ChevronDown} color="common.ui4" />
                        </Clickable>
                    )}
                    <div ref={childernWrapper}>{props.children}</div>
                </StyledWrapper>
            </Hidden>
            <Hidden below={nextBreakpoint}>{props.children}</Hidden>
        </>
    );
};

export default ResponsiveAccordion;
